import React from "react";
import { Link } from "zudoku/components";
import { brand } from "../../brands/index.js";
import { t } from "../../i18n/index.js";

const isMinimal = brand.theme.landingStyle === "minimal";

export function Hero() {
  return isMinimal ? <HeroMinimal /> : <HeroGradient />;
}

/* ─────────────── 出海匠：原有紫色渐变、左右分栏布局 ─────────────── */
function HeroGradient() {
  const s = t.hero;
  return (
    <section
      style={{
        position: "relative",
        margin: "0 0 48px",
        padding: "56px 0 8px",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 60% at 85% 10%, rgba(214, 91, 243, 0.14), transparent 60%), radial-gradient(ellipse 70% 50% at 10% 90%, rgba(108, 77, 255, 0.12), transparent 55%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        className="hero-grid"
        style={{
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: "40px",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 12px",
              fontSize: "12px",
              fontWeight: 500,
              color: "var(--brand-primary)",
              background: "var(--brand-gradient-soft)",
              border: "1px solid color-mix(in oklab, var(--brand-primary) 25%, transparent)",
              borderRadius: "9999px",
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "9999px",
                background: "var(--brand-primary)",
                boxShadow: "0 0 0 3px color-mix(in oklab, var(--brand-primary) 20%, transparent)",
              }}
            />
            {s.badge}
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(34px, 5vw, 56px)",
              lineHeight: 1.08,
              fontWeight: 700,
              letterSpacing: "-0.025em",
              color: "var(--foreground)",
            }}
          >
            {s.titleLine1}
            <br />
            <span
              style={{
                backgroundImage: "var(--brand-gradient)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              {s.titleLine2}
            </span>
          </h1>

          <p
            style={{
              margin: "20px 0 32px",
              maxWidth: "640px",
              fontSize: "18px",
              lineHeight: 1.6,
              color: "var(--muted-foreground)",
            }}
          >
            {s.desc}
          </p>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Link
              to="/quick-start"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 22px",
                fontSize: "15px",
                fontWeight: 500,
                color: "#fff",
                background: "var(--brand-gradient)",
                borderRadius: "var(--radius)",
                boxShadow: "0 8px 24px -8px rgba(124, 92, 255, 0.55)",
                textDecoration: "none",
                transition: "transform 150ms ease, box-shadow 150ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 28px -8px rgba(124, 92, 255, 0.7)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px -8px rgba(124, 92, 255, 0.55)";
              }}
            >
              {s.ctaPrimary}
              <ArrowRightIcon />
            </Link>
            <a
              href={brand.applyApiKeyUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 22px",
                fontSize: "15px",
                fontWeight: 500,
                color: "var(--brand-accent)",
                background: "transparent",
                border: "1px solid color-mix(in oklab, var(--brand-accent) 50%, transparent)",
                borderRadius: "var(--radius)",
                textDecoration: "none",
                transition: "border-color 150ms ease, background 150ms ease, color 150ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--brand-accent)";
                e.currentTarget.style.background =
                  "color-mix(in oklab, var(--brand-accent) 10%, transparent)";
                e.currentTarget.style.color = "var(--brand-accent-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor =
                  "color-mix(in oklab, var(--brand-accent) 50%, transparent)";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--brand-accent)";
              }}
            >
              {s.ctaSecondary}
              <ArrowUpRightIcon />
            </a>
          </div>
        </div>

        <HeroPreview variant="gradient" />
      </div>

      <style>{`
        @media (min-width: 960px) {
          .hero-grid {
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr) !important;
            gap: 56px !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─────────────── Trenz：Stripe/Vercel 式双栏 + 深色 terminal mockup ─────────────── */
function HeroMinimal() {
  const s = t.hero;
  const stats = t.hero.statsRow;
  return (
    <section
      style={{
        position: "relative",
        margin: "0 0 48px",
        // 上方 padding 8px（原 56px）：zudoku 会在 Hero 上方自动渲染 frontmatter
        // title H1，56px 会与 H1 margin-bottom 叠加出过大留空。
        padding: "8px 0 24px",
        overflow: "hidden",
      }}
    >
      {/* subtle background：radial glow + dotted grid */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 50% at 80% 15%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 10% 85%, color-mix(in oklab, var(--brand-primary) 8%, transparent) 0%, transparent 55%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, color-mix(in oklab, var(--foreground) 6%, transparent) 1px, transparent 0)",
          backgroundSize: "32px 32px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 30%, #000 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 60% at 50% 30%, #000 30%, transparent 75%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        className="hero-grid-trenz"
        style={{
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: "48px",
          alignItems: "center",
          paddingTop: "16px",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 10px",
              fontSize: "11.5px",
              fontWeight: 500,
              color: "var(--muted-foreground)",
              background: "color-mix(in oklab, var(--muted) 50%, transparent)",
              border: "1px solid var(--border)",
              borderRadius: "9999px",
              marginBottom: "28px",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            <span
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "9999px",
                background: "var(--brand-primary)",
                boxShadow: "0 0 0 3px color-mix(in oklab, var(--brand-primary) 20%, transparent)",
              }}
            />
            {s.badge}
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(38px, 5.2vw, 68px)",
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: "-0.04em",
              color: "var(--foreground)",
            }}
          >
            {s.titleLine1}
            <br />
            {s.titleLine2}
          </h1>

          <p
            style={{
              margin: "24px 0 36px",
              maxWidth: "560px",
              fontSize: "17px",
              lineHeight: 1.55,
              color: "var(--muted-foreground)",
              fontWeight: 400,
            }}
          >
            {s.desc}
          </p>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Link
              to="/quick-start"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 22px",
                fontSize: "14.5px",
                fontWeight: 500,
                color: "#fff",
                background: "var(--brand-primary)",
                borderRadius: "9999px",
                textDecoration: "none",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.15) inset, 0 10px 24px -10px color-mix(in oklab, var(--brand-primary) 60%, transparent)",
                transition: "transform 150ms ease, box-shadow 150ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 1px 0 rgba(255,255,255,0.18) inset, 0 14px 28px -10px color-mix(in oklab, var(--brand-primary) 75%, transparent)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow =
                  "0 1px 0 rgba(255,255,255,0.15) inset, 0 10px 24px -10px color-mix(in oklab, var(--brand-primary) 60%, transparent)";
              }}
            >
              {s.ctaPrimary}
              <ArrowRightIcon />
            </Link>
            <a
              href={brand.applyApiKeyUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "12px 20px",
                fontSize: "14.5px",
                fontWeight: 500,
                color: "var(--foreground)",
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: "9999px",
                textDecoration: "none",
                transition: "background 150ms ease, border-color 150ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--muted)";
                e.currentTarget.style.borderColor =
                  "color-mix(in oklab, var(--foreground) 30%, var(--border))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
            >
              {s.ctaSecondary}
              <ArrowUpRightIcon />
            </a>
          </div>

          {/* Stats row */}
          <div
            style={{
              marginTop: "44px",
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, auto))",
              gap: "32px",
              maxWidth: "520px",
            }}
          >
            <StatCol value={stats.col1Value} label={stats.col1Label} />
            <StatCol value={stats.col2Value} label={stats.col2Label} />
            <StatCol value={stats.col3Value} label={stats.col3Label} />
          </div>
        </div>

        <TerminalMockup />
      </div>

      <style>{`
        @media (min-width: 960px) {
          .hero-grid-trenz {
            grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) !important;
            gap: 56px !important;
            padding-top: 40px !important;
          }
        }
      `}</style>
    </section>
  );
}

function StatCol({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ minWidth: 0, textAlign: "center" }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "22px",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "var(--foreground)",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: "4px",
          fontSize: "12px",
          lineHeight: 1.4,
          color: "var(--muted-foreground)",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* 深色 macOS 终端 mockup：真实 curl 请求 + JSON 响应 */
function TerminalMockup() {
  const host = brand.gatewayHost;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        borderRadius: "14px",
        padding: "1px",
        background:
          "linear-gradient(135deg, color-mix(in oklab, var(--brand-primary) 40%, transparent) 0%, color-mix(in oklab, var(--brand-accent) 30%, transparent) 100%)",
        boxShadow:
          "0 30px 60px -28px rgba(10, 132, 255, 0.35), 0 10px 30px -16px rgba(0, 0, 0, 0.3)",
      }}
    >
      <div
        style={{
          borderRadius: "13px",
          background: "#0b0f17",
          overflow: "hidden",
        }}
      >
        {/* macOS title bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "11px 14px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            background: "linear-gradient(180deg, #151a24 0%, #0e131c 100%)",
          }}
        >
          <div style={{ display: "flex", gap: "6px" }}>
            <span style={{ width: 11, height: 11, borderRadius: 999, background: "#ff5f56" }} />
            <span style={{ width: 11, height: 11, borderRadius: 999, background: "#ffbd2e" }} />
            <span style={{ width: 11, height: 11, borderRadius: 999, background: "#27c93f" }} />
          </div>
          <div
            style={{
              flex: 1,
              fontSize: "11.5px",
              fontFamily: "var(--font-mono)",
              color: "#94a3b8",
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            {host}
          </div>
          <div style={{ width: 42 }} />
        </div>

        {/* Terminal body */}
        <div
          style={{
            padding: "16px 18px 18px",
            fontFamily: "var(--font-mono)",
            fontSize: "12.5px",
            lineHeight: 1.65,
            color: "#e2e8f0",
          }}
        >
          <div style={{ color: "#7dd3fc" }}>
            <span style={{ color: "#64748b", userSelect: "none" }}>$</span>{" "}
            <span style={{ color: "#86efac" }}>curl</span>{" "}
            <span style={{ color: "#cbd5e1" }}>-X GET</span>{" "}
            <span style={{ color: "#fde68a" }}>
              "https://{host}/open/v1/products/search?keyword=phone+case&country=us"
            </span>
          </div>
          <div style={{ color: "#cbd5e1", paddingLeft: "16px" }}>
            <span style={{ color: "#cbd5e1" }}>-H</span>{" "}
            <span style={{ color: "#fde68a" }}>"Authorization: Bearer sk_live_xxx"</span>
          </div>
          <div
            style={{
              marginTop: "14px",
              paddingTop: "10px",
              borderTop: "1px dashed rgba(255,255,255,0.08)",
              color: "#64748b",
              fontSize: "11px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            HTTP/1.1 200 OK
          </div>
          <pre
            style={{
              margin: "6px 0 0",
              padding: 0,
              background: "transparent",
              fontFamily: "inherit",
              fontSize: "12.5px",
              color: "#e2e8f0",
              whiteSpace: "pre",
              overflowX: "auto",
            }}
          >
{`{
  "data": {
    "items": [
      {
        "id": "`}<span style={{ color: "#fde68a" }}>"1729846273"</span>{`,
        "title": `}<span style={{ color: "#86efac" }}>"Silicone Phone Case"</span>{`,
        "price": `}<span style={{ color: "#7dd3fc" }}>4.99</span>{`,
        "sales": `}<span style={{ color: "#7dd3fc" }}>52300</span>{`
      }
    ],
    "total_count": `}<span style={{ color: "#7dd3fc" }}>1580</span>{`
  }
}`}
          </pre>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── HeroPreview 共用 ─────────────── */

const methodChip: Record<"GET" | "POST", React.CSSProperties> = {
  GET: {
    color: "#16a34a",
    background: "color-mix(in oklab, oklch(0.65 0.18 145) 14%, transparent)",
  },
  POST: {
    color: "var(--brand-primary)",
    background: "color-mix(in oklab, var(--brand-primary) 14%, transparent)",
  },
};

function HeroPreview({ variant }: { variant: "gradient" | "minimal" }) {
  const caps = t.hero.capabilityTags;
  const stats = t.hero.stats;
  const capabilities: { method: "GET" | "POST"; path: string; tag: string }[] = [
    { method: "GET", path: "/open/v1/products/search", tag: caps.ecommerce },
    { method: "POST", path: "/open/v1/ai/sd-pro/video/generation", tag: caps.aiVideo },
    { method: "POST", path: "/open/v1/social/accounts/list", tag: caps.social },
    { method: "POST", path: "/open/v1/ads/campaigns/list", tag: caps.ads },
  ];

  const shellStyle: React.CSSProperties =
    variant === "gradient"
      ? {
          position: "relative",
          borderRadius: "calc(var(--radius) + 4px)",
          padding: "1px",
          background:
            "linear-gradient(140deg, color-mix(in oklab, var(--brand-primary) 55%, transparent) 0%, color-mix(in oklab, oklch(0.7 0.18 320) 35%, transparent) 100%)",
          boxShadow:
            "0 30px 60px -28px rgba(124, 92, 255, 0.45), 0 8px 24px -16px rgba(214, 91, 243, 0.35)",
        }
      : {
          position: "relative",
          borderRadius: "calc(var(--radius) + 4px)",
          padding: "1px",
          background: "var(--border)",
          boxShadow:
            "0 24px 50px -30px rgba(0, 0, 0, 0.25), 0 4px 16px -8px rgba(0, 0, 0, 0.08)",
        };

  const innerStyle: React.CSSProperties = {
    borderRadius: "calc(var(--radius) + 3px)",
    background: "var(--card)",
    overflow: "hidden",
  };

  return (
    <div aria-hidden="true" style={shellStyle}>
      <div style={innerStyle}>
        {/* Title bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            borderBottom: "1px solid var(--border)",
            background: "color-mix(in oklab, var(--muted) 50%, transparent)",
          }}
        >
          <div style={{ display: "flex", gap: "6px" }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: "#ff5f56" }} />
            <span style={{ width: 10, height: 10, borderRadius: 999, background: "#ffbd2e" }} />
            <span style={{ width: 10, height: 10, borderRadius: 999, background: "#27c93f" }} />
          </div>
          <div
            style={{
              flex: 1,
              fontSize: "11.5px",
              fontFamily: "var(--font-mono)",
              color: "var(--muted-foreground)",
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            {brand.gatewayHost}
          </div>
          <div style={{ width: 42 }} />
        </div>

        {/* Endpoint list */}
        <div style={{ padding: "8px" }}>
          {capabilities.map((c) => (
            <div
              key={c.path}
              style={{
                display: "grid",
                gridTemplateColumns: "auto minmax(0, 1fr) auto",
                alignItems: "center",
                gap: "10px",
                padding: "10px 12px",
                borderRadius: "calc(var(--radius) - 4px)",
              }}
            >
              <span
                style={{
                  padding: "2px 7px",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  borderRadius: "4px",
                  fontFamily: "var(--font-mono)",
                  ...methodChip[c.method],
                }}
              >
                {c.method}
              </span>
              <code
                style={{
                  fontSize: "12px",
                  fontFamily: "var(--font-mono)",
                  color: "var(--foreground)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.path}
              </code>
              <span
                style={{
                  padding: "1px 8px",
                  fontSize: "10.5px",
                  color: "var(--muted-foreground)",
                  background: "var(--muted)",
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                }}
              >
                {c.tag}
              </span>
            </div>
          ))}
        </div>

        {/* Footer stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            borderTop: "1px solid var(--border)",
            background:
              variant === "gradient"
                ? "linear-gradient(180deg, transparent 0%, color-mix(in oklab, var(--brand-primary) 4%, transparent) 100%)"
                : "transparent",
          }}
        >
          <Stat value={stats.tiktokValue} label={stats.tiktokLabel} variant={variant} />
          <Stat value={stats.seedanceValue} label={stats.seedanceLabel} variant={variant} divider />
          <Stat value={stats.multiPlatformValue} label={stats.multiPlatformLabel} variant={variant} divider />
        </div>
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  divider,
  variant,
}: {
  value: string;
  label: string;
  divider?: boolean;
  variant: "gradient" | "minimal";
}) {
  const valueStyle: React.CSSProperties =
    variant === "gradient"
      ? {
          fontSize: "16px",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          backgroundImage: "var(--brand-gradient)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          color: "transparent",
          whiteSpace: "nowrap",
        }
      : {
          fontSize: "15px",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--foreground)",
          whiteSpace: "nowrap",
        };

  return (
    <div
      style={{
        padding: "14px 10px",
        textAlign: "center",
        borderLeft: divider ? "1px solid var(--border)" : "none",
        minWidth: 0,
      }}
    >
      <div style={valueStyle}>{value}</div>
      <div
        style={{
          marginTop: "4px",
          fontSize: "11px",
          lineHeight: 1.4,
          color: "var(--muted-foreground)",
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* 共用小图标 */
function ArrowRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function ArrowUpRightIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17L17 7M7 7h10v10" />
    </svg>
  );
}
